/**
 * Port of legacy `LogicOperand` (duplicated verbatim in three legacy files —
 * `momentum-edit.component.ts`, `indicatorcondition-edit.component.ts`, and
 * a dead copy in `coinpairlist-edit.component.ts`): the type/field/value/
 * index model behind a `Momentum`/`IndicatorCondition`'s `logicOperand1`/
 * `logicOperand2`, and its exact `toString()` persistence format. Kept as a
 * single shared module here (this app's screener domain, not
 * `grabbitbot-legacy-ui` — the picker UI isn't shared with any other
 * rewritten app) so `MomentumEditPage` and `IndicatorconditionEditPage`
 * don't each hand-roll the same parse/serialize logic.
 */
export type LogicOperandType = '' | 'indicator' | 'candle' | 'lastValue' | 'constant'

export interface LogicOperand {
  type: LogicOperandType
  field: string
  value: string
  index: number
}

export function emptyLogicOperand(): LogicOperand {
  return { type: '', field: '', value: '', index: 0 }
}

/** Legacy `LogicOperand.toString()` — the exact string persisted as
 * `logicOperand1`/`logicOperand2` (`indicator.field.0`, `candle.close.0`,
 * `lastValue.close`, `constant.5`). Unknown/empty type serializes to `''`,
 * matching legacy's `default: return ''`. */
export function logicOperandToString(operand: LogicOperand): string {
  switch (operand.type) {
    case 'indicator':
    case 'candle':
      return `${operand.type}.${operand.field}.${operand.index ? operand.index : '0'}`
    case 'lastValue':
      return `${operand.type}.${operand.field}`
    case 'constant':
      return `${operand.type}.${operand.value}`
    default:
      return ''
  }
}

/** Legacy `buildLogicOperand()` — parses a persisted operand string back
 * into the picker model. */
export function parseLogicOperand(operand: string | null | undefined): LogicOperand {
  const result = emptyLogicOperand()
  if (!operand) return result

  const parts = operand.split('.')
  const type = parts[0] as LogicOperandType
  result.type = type

  switch (type) {
    case 'indicator':
    case 'candle':
      if (parts.length > 1) result.field = parts[1] ?? ''
      result.index = parts.length > 2 ? parseInt(parts[2] ?? '0', 10) || 0 : 0
      break
    case 'lastValue':
      if (parts.length > 1) result.field = parts[1] ?? ''
      break
    case 'constant':
      result.value = parts.slice(1).join('.')
      break
    default:
      break
  }

  return result
}

/** Legacy `generateConditionText()` (duplicated in `momentum-edit` and
 * `indicatorcondition-edit`): the read-only rendering of a condition once
 * it's saved — `momentum.constant.` / `indicator.` prefixes are stripped for
 * readability the same way legacy did. */
export function generateConditionText(params: {
  indicator?: string | null
  source?: string | null
  logic?: string | null
  logicOperand1?: string | null
  logicOperand2?: string | null
}): string {
  const { indicator, source, logic, logicOperand1, logicOperand2 } = params
  let result = `${logicOperand1 ?? ''} `
  switch (logic) {
    case 'lt':
      result += '<'
      break
    case 'lte':
      result += '<='
      break
    case 'gt':
      result += '>'
      break
    case 'gte':
      result += '>='
      break
    case 'eq':
      result += '='
      break
    case 'ne':
      result += '!='
      break
    case 'cross':
      result += 'crosses'
      break
    case 'crossBelow':
      result += 'crosses from below'
      break
    case 'crossAbove':
      result += 'crosses from above'
      break
    default:
      result += logic ?? ''
      break
  }
  result += ` ${logicOperand2 ?? ''}`
  result = result.replace(/constant\./g, '')
  result = result.replace(/indicator\./g, `${indicator ?? ''}.`)
  if (source === 'cl') {
    result = result.replace(/candle\./g, '')
  }
  return result
}

export const LOGIC_OPTIONS: { value: string; label: string }[] = [
  { value: 'lt', label: '< less than' },
  { value: 'lte', label: '<= less than or equal' },
  { value: 'gt', label: '> greater than' },
  { value: 'gte', label: '>= greater than or equal' },
  { value: 'eq', label: '= equal' },
  { value: 'ne', label: '!= not equal' },
  { value: 'cross', label: 'crosses' },
  { value: 'crossBelow', label: 'crosses from below' },
  { value: 'crossAbove', label: 'crosses from above' },
]
